import { useTranslation } from "next-i18next";
import { PurchaseTradeIn } from "@modules/purchase/types/PurchaseTradeIn";
import classNames from "classnames";
import PurchaseInformationListItem from "@modules/purchase/components/aftermarket/PurchaseInformationListItem";
import { formatCurrency } from "@talladega/helpers";
import { useRouter } from "next/router";
import { VehicleAvatar } from "@talladega/components";
import PurchaseSourceVehicleImage from "../PurchaseSourceVehicleImage";

type Props = {
  tradeIn: PurchaseTradeIn;
};

export const InstantCashOfferSummaryHeader = ({ tradeIn }: Props) => {
  const router = useRouter();
  const { t } = useTranslation(["purchase", "common"]);
  const locale = router.locale || "en";
  const { vin, finalOfferPrice } = tradeIn;

  return (
    <>
      <ul
        className={classNames({
          "border-t border-gray-100": !!vin,
        })}
      >
        {vin && (
          <PurchaseInformationListItem
            key="vehicle-vin"
            label={t("common:vin")}
            value={vin}
          />
        )}
      </ul>

      <ul
        className={classNames({
          "border-t border-gray-100": !!finalOfferPrice,
        })}
      >
        {finalOfferPrice && (
          <PurchaseInformationListItem
            key="final-offer"
            label={t("purchase:instant_cash_offer_value")}
            value={formatCurrency(finalOfferPrice, locale, {
              showCents: "never",
            })}
            isHighlighted
          />
        )}
      </ul>
    </>
  );
};

const InstantCashOfferSummary = ({ tradeIn }: Props) => {
  const { t } = useTranslation(["purchase", "common"]);
  const { year, make, model, trim, mileage } = tradeIn;
  const ymm = [year, make, model].filter(Boolean).join(" ");
  const trimMileage = [
    trim,
    `${mileage?.toLocaleString()} ${t("common:kilometers")}`,
  ]
    .filter(Boolean)
    .join(" | ");
  const coverImageId = tradeIn?.imageList?.split(",")[0];
  const coverImageSrc = `${process.env.NEXT_PUBLIC_API}/api/trade-in/${tradeIn.id}/vehicle/image?fileName=${coverImageId}`;

  return (
    <div className="hidden lg:flex flex-col gap-5 items-left mx-12 mb-12 mt-6">
      <div className="flex flex-col gap-3 h-full w-full">
        {coverImageId && (
          <div className="pb-6">
            <PurchaseSourceVehicleImage
              className="rounded-xl w-full max-h-56 object-cover object-center"
              src={coverImageSrc}
              alt="Appraisal vehicle picture"
            />
          </div>
        )}
        <div className="flex gap-3 items-center">
          <VehicleAvatar
            size="sm"
            year={year || undefined}
            make={make || undefined}
          />
          <div className="flex flex-col gap-3">
            <h3>{ymm}</h3>
            <p className="text-text-light-200 uppercase">{trimMileage}</p>
          </div>
        </div>

        <InstantCashOfferSummaryHeader tradeIn={tradeIn} />
      </div>
    </div>
  );
};

export default InstantCashOfferSummary;
